import React, { useState, useEffect, useRef } from "react";
import Icon from "./Icon";
import Button from "./Button";
import { isMobile } from "react-device-detect";

type DropdownOption = {
  label: string;
  onClick: () => void;
  icon?: string;
  disabled?: boolean;
  customClassButtonGTM?: string;
};

type DropdownMenuProps = {
  options: DropdownOption[];
  style?: any;
  className?: string;
  triggerIcon?: string;
  triggerLabel?: string;
  triggerColor?: "info" | "primary" | "negative";
  triggerSize?: string;
  position?: "left" | "right";
  triggerFlat?: boolean;
  truncate?: boolean;
  disabled?: boolean;
  customPadding?: boolean;
  showNotificationDot?: boolean;
};

const DropdownMenu: React.FC<DropdownMenuProps> = ({
  options,
  customPadding = false,
  className = "",
  triggerIcon = "arrow-tip-down",
  triggerLabel = "",
  triggerColor = "info",
  position = "right",
  disabled = false,
  truncate = false,
  triggerSize = "20px",
  triggerFlat,
  showNotificationDot = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Close dropdown when clicking outside or scrolling
  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    const handleScroll = () => setIsOpen(false);

    if (isOpen) {
      document.addEventListener("mousedown", handleOutsideClick);
      window.addEventListener("scroll", handleScroll, { passive: true });
    }

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      window.removeEventListener("scroll", handleScroll);
    };
  }, [isOpen]);

  // Calculate dropdown position
  const getDropdownPosition = () => {
    if (!triggerRef.current || !dropdownRef.current) return {};

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const dropdownWidth = dropdownRef.current.offsetWidth;
    const viewportWidth = window.innerWidth;

    if (position === "right") {
      const spaceRight = viewportWidth - triggerRect.right;
      return {
        left: spaceRight >= dropdownWidth ? "auto" : "0",
        right: spaceRight >= dropdownWidth ? "0" : "auto",
      };
    } else {
      const spaceLeft = triggerRect.left;
      return {
        left: spaceLeft >= dropdownWidth ? "0" : "auto",
        right: spaceLeft >= dropdownWidth ? "auto" : "0",
      };
    }
  };

  return (
    <div className={`relative inline-block ${className}`}>
      {/* Trigger Button con badge */}
      <div className="relative inline-block">
        <Button
          flat={triggerFlat}
          refs={triggerRef}
          onClick={(e) => {
            e.stopPropagation();
            if (!disabled) setIsOpen(!isOpen);
          }}
          padding={customPadding ? "nopad" : "md"}
          disabled={disabled}
          className={`flex items-center gap-2 ${
            customPadding ? "px-0" : ""
          } p-2 rounded-md hover:bg-gray-100 transition-colors ${
            disabled ? "opacity-50 cursor-not-allowed" : ""
          }`}
          color={triggerColor}
          icon={triggerIcon}
          iconSize={triggerSize}
          label={triggerLabel}
        />
        {showNotificationDot && (
          <span className="absolute top-1 -left-1 h-3 w-3 rounded-full bg-red-700 border border-white" />
        )}
      </div>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          ref={dropdownRef}
          className={`absolute z-50 mt-1 bg-white shadow-lg rounded-md ${
            isMobile ? "-left-44" : ""
          } py-1 min-w-[160px] border border-gray-200 ${
            position === "right" ? "right-0" : "left-0"
          }`}
          style={{
            ...getDropdownPosition(),
            maxWidth: "calc(100vw - 32px)",
            zIndex: "999999",
          }}
        >
          {options.map((option, index) => (
            <button
              key={`${option.label}-${index}`}
              onClick={(e) => {
                e.stopPropagation();
                option.onClick();
                setIsOpen(false);
              }}
              disabled={option.disabled}
              className={`w-full text-left px-4 py-2 flex items-center gap-3 ${
                option.disabled
                  ? "text-gray-400 cursor-not-allowed"
                  : "hover:bg-gray-100 text-gray-800"
              } ${option.customClassButtonGTM || ""}`}
            >
              {option.icon && (
                <div>
                  <Icon name={option.icon} size="18px" />
                </div>
              )}
              <span
                className={`whitespace-nowrap ${
                  isMobile ? (truncate ? "truncate" : "") : ""
                }`}
              >
                {option.label}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default DropdownMenu;
